import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FileText } from "lucide-react";
import { 
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { 
  summaryFormSchema, 
  SummaryFormValues,
  summaryDefaultValues,
  REGION_OPTIONS,
  TEAM_OPTIONS,
} from "../schemas/summaryFormSchema";

interface SummaryFormProps {
  onSectionComplete: (nextSection: string) => void;
}

export const SummaryForm = ({ onSectionComplete }: SummaryFormProps) => {
  const { toast } = useToast();
  
  // Initialize the summary form
  const form = useForm<SummaryFormValues>({
    resolver: zodResolver(summaryFormSchema),
    defaultValues: summaryDefaultValues,
  });

  // Load saved data from localStorage on component mount
  useEffect(() => {
    const savedData = localStorage.getItem("proposal-summary");
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData) as SummaryFormValues;
        form.reset(parsedData);
      } catch (error) {
        console.error("Error parsing saved summary data:", error);
      }
    }
  }, [form]);

  // Form submission handler
  function onSubmit(data: SummaryFormValues) {
    console.log("Summary form submitted:", data);
    // Save the form data to local storage or state management
    localStorage.setItem("proposal-summary", JSON.stringify(data));
    
    // Show success toast
    toast({
      title: "Summary saved",
      description: "Moving to the Pricing section",
    });
    
    // Navigate to the next section
    onSectionComplete("pricing");
  }

  return (
    <div className="transition-opacity">
      <div className="flex items-center mb-4">
        <FileText className="h-6 w-6 text-black mr-2" />
        <h2 className="text-xl font-semibold text-gray-800">Summary</h2>
      </div>
      <p className="text-gray-600 mb-6">Create a summary of your RFP/Instructions</p>
      
      <Card className="mt-4">
        <CardContent className="pt-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>RFP/Instructions Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter RFP/Instructions title" {...field} />
                    </FormControl>
                    <FormDescription>
                      This will be the main title of your RFP/Instructions.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="matterName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Matter Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter matter name" {...field} />
                    </FormControl>
                    <FormDescription>
                      The name of the matter or project this RFP/Instructions relates to.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="region"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Region</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select region" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="bg-white border shadow-lg z-50">
                          {REGION_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        The geographic region for this matter.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="team"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Team</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select team" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="bg-white border shadow-lg z-50">
                          {TEAM_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Your relevant team.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Enter a detailed description of your RFP/Instructions"
                        className="min-h-[120px]"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Provide a clear summary of what your RFP/Instructions offers.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="includeCompanyInfo"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Include company information</FormLabel>
                      <FormDescription>
                        Automatically add your company details to this RFP/Instructions.
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
              
              <div className="flex justify-end">
                <Button type="submit">Save & Continue</Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};
